// RequestLoan.jsx
import React, { useEffect, useMemo, useState } from "react";
import MemberLayout from "./MemberLayout";
import "../../UnifiedTheme.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

function safeParseJSON(v) {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}

function getLoggedInMemberIdFromStorage() {
  const direct =
    localStorage.getItem("hopeSaccoMemberId") ||
    localStorage.getItem("hopeSaccoMemberID") ||
    localStorage.getItem("memberId");

  if (direct) return direct;

  const user =
    safeParseJSON(localStorage.getItem("hopeSaccoUser")) ||
    safeParseJSON(localStorage.getItem("user")) ||
    safeParseJSON(localStorage.getItem("member"));

  if (user?.memberId) return user.memberId;

  // Try to decode JWT payload (no verification, just read claims)
  const token = localStorage.getItem("hopeSaccoToken");
  if (token && token.split(".").length >= 2) {
    try {
      const payload = token.split(".")[1];
      const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
      return json?.memberId || json?.member?.memberId || json?.profile?.memberId || null;
    } catch {
      return null;
    }
  }

  return null;
}

function money(n) {
  const x = Number(n || 0);
  return x.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function pct(n) {
  const x = Number(n);
  if (!isFinite(x)) return "0%";
  return `${Math.round(x)}%`;
}

async function fetchJson(url, token) {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(url, { headers });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

function LoanCredibilityPanel() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [memberId, setMemberId] = useState(null);

  const [loanStatus, setLoanStatus] = useState(null);
  const [projectedShares, setProjectedShares] = useState(null);

  const token = useMemo(() => localStorage.getItem("hopeSaccoToken"), []);
  const TODAY_ISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // ✅ FIX: resolve memberId the same reliable way as the system should:
  // 1) localStorage (fast)
  // 2) /auth/profile (source of truth, requires token)
  useEffect(() => {
    let alive = true;

    async function resolveMemberId() {
      setErr("");

      const fromStorage = getLoggedInMemberIdFromStorage();
      if (fromStorage) {
        if (!alive) return;
        setMemberId(fromStorage);
        // keep a stable key for future pages
        localStorage.setItem("hopeSaccoMemberId", fromStorage);
        return;
      }

      if (!token) {
        if (!alive) return;
        setErr("Member ID not found. Please log out and log in again.");
        setMemberId(null);
        return;
      }

      const prof = await fetchJson(`${API_URL}/auth/profile`, token);
      if (!alive) return;

      if (!prof.ok) {
        setErr("Member ID not found. Please log out and log in again.");
        setMemberId(null);
        return;
      }

      const mid = prof?.data?.data?.memberId || null;
      if (!mid) {
        setErr("Member ID not found. Please log out and log in again.");
        setMemberId(null);
        return;
      }

      localStorage.setItem("hopeSaccoMemberId", mid);
      setMemberId(mid);
    }

    resolveMemberId();
    return () => {
      alive = false;
    };
  }, [token]);

  // Load loan credibility automatically (no button click)
  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErr("");

      if (!memberId) {
        setLoading(false);
        // err already set by resolver effect if needed
        return;
      }

      try {
        const [ls, ps] = await Promise.all([
          fetchJson(`${API_URL}/loans/members/${encodeURIComponent(memberId)}/loan-status`, token),
          fetchJson(
            `${API_URL}/loans/members/${encodeURIComponent(
              memberId
            )}/projected-shares?targetDate=${encodeURIComponent(TODAY_ISO)}`,
            token
          ),
        ]);

        if (!alive) return;

        if (!ls.ok) {
          const msg = ls?.data?.message || ls?.data?.error || "Failed to load your loan status.";
          throw new Error(msg);
        }

        // projected shares might be optional; don’t hard fail
        if (ps.ok && ps?.data?.success) {
          setProjectedShares(ps.data.data);
        } else {
          setProjectedShares(null);
        }

        // ✅ loan-status controller returns "percentagePaid" (not "repaymentPercentage")
        const statusData = ls?.data?.data || null;

        // normalize for UI
        const normalized = statusData
          ? {
              ...statusData,
              activeLoans: (statusData.activeLoans || []).map((l) => ({
                ...l,
                repaymentPercentage:
                  l.repaymentPercentage ?? l.percentagePaid ?? l.percentage ?? 0,
              })),
            }
          : null;

        setLoanStatus(normalized);
      } catch (e) {
        setErr(e?.message || "Failed to load loan credibility.");
      } finally {
        setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [memberId, token, TODAY_ISO]);

  const trulyActiveLoans = useMemo(() => {
    const a = loanStatus?.activeLoans || [];
    // only loans with balance > 0 are truly active for eligibility & totals
    return a.filter((l) => Number(l?.currentBalance || 0) > 0);
  }, [loanStatus]);

  const totalOutstandingDebt = useMemo(() => {
    return trulyActiveLoans.reduce((sum, l) => sum + Number(l?.currentBalance || 0), 0);
  }, [trulyActiveLoans]);

  const borrowingCapacity = useMemo(() => {
    return projectedShares ? Number(projectedShares.borrowingCapacity || 0) : null;
  }, [projectedShares]);

  const remainingCapacity = useMemo(() => {
    if (borrowingCapacity === null) return null;
    const rem = borrowingCapacity - totalOutstandingDebt;
    return Math.max(0, rem);
  }, [borrowingCapacity, totalOutstandingDebt]);

  function getActiveLoansForType(loanType) {
    return trulyActiveLoans.filter((l) => (l?.loanType || "") === loanType);
  }

  function canApplyForAnotherOfType(loanType) {
    const active = getActiveLoansForType(loanType);

    if (active.length === 0) {
      return { ok: true, title: "No active loan", reason: "You have no active loan of this type." };
    }

    const qualifying = active.filter((l) => !!l?.qualifiesForTopUp);
    if (qualifying.length > 0) {
      return {
        ok: true,
        title: "Eligible",
        reason: "You have paid ≥ 75% of at least one active loan of this type (top-up allowed).",
        loans: qualifying,
      };
    }

    // show best % paid so user understands
    const best = Math.max(...active.map((l) => Number(l.repaymentPercentage || 0)));
    return {
      ok: false,
      title: "Not eligible",
      reason: `You have active loans of this type, but repayment is below 75% (best so far: ${pct(best)}).`,
      loans: active,
    };
  }

  const types = useMemo(
    () => [
      { key: "normal", label: "Normal Loan" },
      { key: "school", label: "School Fees Loan" },
      { key: "emergency", label: "Emergency Loan" },
    ],
    []
  );

  const overallConclusion = useMemo(() => {
    if (!loanStatus) return null;

    const byType = types.map((t) => ({ type: t, res: canApplyForAnotherOfType(t.key) }));

    // capacity exhausted => hard block
    if (remainingCapacity !== null && remainingCapacity <= 0) {
      return {
        ok: false,
        headline: "You do NOT qualify at the moment.",
        detail:
          "Your borrowing capacity is fully used (total outstanding debt has reached your limit). Reduce your outstanding balance to borrow again.",
        byType,
      };
    }

    const anyEligible = byType.some((x) => x.res.ok);

    if (!anyEligible) {
      return {
        ok: false,
        headline: "You do NOT qualify at the moment.",
        detail:
          "Your active loans are below the 75% repayment threshold required for additional borrowing (top-up).",
        byType,
      };
    }

    return {
      ok: true,
      headline: "You qualify to borrow (depending on loan type).",
      detail:
        remainingCapacity === null
          ? "Loan-type eligibility is shown below. Borrowing capacity could not be confirmed, so ensure your total outstanding debt stays within your limit."
          : "Loan-type eligibility is shown below. You also have remaining borrowing capacity.",
      byType,
    };
  }, [loanStatus, remainingCapacity, types]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {loading ? (
        <div className="tabs-container">
          <div className="tab-content">
            <div className="info-box-muted">
              <div className="info-box-title">Loading loan credibility…</div>
              <p>Please wait as we check your loans, repayment progress and shares capacity.</p>
            </div>
          </div>
        </div>
      ) : err ? (
        <div className="tabs-container">
          <div className="tab-content">
            <div className="info-box-danger">
              <div className="info-box-title">Loan credibility failed</div>
              <p>{err}</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Summary (UnifiedTheme.css compact info boxes) */}
          <div className="info-box-primary">
            <div style={{ width: "100%" }}>
              <div className="info-box-title">Shares & Capacity Summary</div>

              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Member ID:</span>
                  <span className="info-value">{memberId}</span>
                </div>

                <div className="info-item">
                  <span className="info-label">Projected Shares (as at {TODAY_ISO}):</span>
                  <span className="info-value info-value-large">
                    {projectedShares ? money(projectedShares.projectedShares) : "—"}
                  </span>
                </div>

                <div className="info-item">
                  <span className="info-label">Borrowing Capacity (Shares × 3):</span>
                  <span className="info-value info-value-large">
                    {borrowingCapacity === null ? "—" : money(borrowingCapacity)}
                  </span>
                </div>

                <div className="info-item">
                  <span className="info-label">Outstanding Debt (All Active Loans):</span>
                  <span className="info-value info-value-large">{money(totalOutstandingDebt)}</span>
                </div>

                <div className="info-item">
                  <span className="info-label">Remaining Capacity:</span>
                  <span className="info-value info-value-large">
                    {remainingCapacity === null ? "—" : money(remainingCapacity)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Conclusion */}
          {overallConclusion && (
            <div className={overallConclusion.ok ? "info-box-primary" : "info-box-warning"}>
              <div style={{ width: "100%" }}>
                <div className="info-box-title">
                  {overallConclusion.ok ? "✅ Conclusion" : "⚠️ Conclusion"}
                </div>
                <p style={{ marginBottom: 10 }}>
                  <b>{overallConclusion.headline}</b>
                </p>
                <p>{overallConclusion.detail}</p>
              </div>
            </div>
          )}

          {/* Per-loan-type breakdown */}
          <div className="tabs-container">
            <div className="tab-content" style={{ minHeight: "unset" }}>
              <div className="page-header" style={{ marginBottom: 18, padding: "10px 0" }}>
                <div className="header-left">
                  <h1 style={{ fontSize: 22, marginBottom: 6 }}>Loan Credibility</h1>
                  <div className="breadcrumb" style={{ marginTop: 0 }}>
                    Automatically checks your active loans, repayment %, and shares-based capacity.
                  </div>
                </div>
              </div>

              {types.map((t) => {
                const res = canApplyForAnotherOfType(t.key);
                const loans = res.loans || [];
                const activeCount = getActiveLoansForType(t.key).length;

                return (
                  <div key={t.key} className={res.ok ? "info-box-primary" : "info-box-warning"}>
                    <div style={{ width: "100%" }}>
                      <div className="info-box-title">
                        {t.label} —{" "}
                        <span className={res.ok ? "status-success" : "status-danger"}>
                          {res.title}
                        </span>
                      </div>

                      <div className="info-grid">
                        <div className="info-item">
                          <span className="info-label">Active loans of this type:</span>
                          <span className="info-value">{activeCount}</span>
                        </div>

                        <div className="info-item">
                          <span className="info-label">Rule:</span>
                          <span className="info-value">Top-up allowed if ≥ 75% paid</span>
                        </div>
                      </div>

                      <p style={{ marginTop: 10 }}>{res.reason}</p>

                      {loans.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          {loans.map((l, idx) => (
                            <div key={l.loanId || `${t.key}-${idx}`} className="loan-card">
                              <div className="loan-grid">
                                <div className="loan-item">
                                  <span className="loan-label">Loan #</span>
                                  <span className="loan-value">{idx + 1}</span>
                                </div>

                                <div className="loan-item">
                                  <span className="loan-label">Original</span>
                                  <span className="loan-value">{money(l.originalAmount)}</span>
                                </div>

                                <div className="loan-item">
                                  <span className="loan-label">Balance</span>
                                  <span className="loan-value">{money(l.currentBalance)}</span>
                                </div>

                                <div className="loan-item">
                                  <span className="loan-label">Paid</span>
                                  <span className="loan-progress">{pct(l.repaymentPercentage)}</span>
                                </div>

                                <div className="loan-item">
                                  <span className="loan-label">Top-up</span>
                                  <span className="loan-value">
                                    {l.qualifiesForTopUp ? "✅ Yes" : "❌ No"}
                                  </span>
                                </div>
                              </div>

                              <div className="loan-status">
                                <span className={l.qualifiesForTopUp ? "status-success" : "status-danger"}>
                                  {l.qualifiesForTopUp
                                    ? "Eligible for top-up on this loan."
                                    : "Not yet eligible for top-up on this loan."}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function RequestLoan() {
  const [tab, setTab] = useState("credibility"); // 'credibility' | 'request'

  return (
    <MemberLayout>
      <div className="apply-loan-page">
        <div className="page-header">
          <div className="header-left">
            <h1>Loans</h1>
            <div className="breadcrumb">
              Check your loan credibility before requesting a loan.
            </div>
          </div>
        </div>

        {/* Unified CSS tabs */}
        <div className="tabs-container">
          <div className="tabs-nav">
            <button
              type="button"
              className={`tab ${tab === "credibility" ? "active" : ""}`}
              onClick={() => setTab("credibility")}
            >
              Check Loan Credibility
            </button>
            <button
              type="button"
              className={`tab ${tab === "request" ? "active" : ""}`}
              onClick={() => setTab("request")}
            >
              Request Loan
            </button>
          </div>

          <div className="tab-content">
            {tab === "credibility" ? (
              <LoanCredibilityPanel />
            ) : (
              <div className="info-box-muted">
                <div style={{ width: "100%" }}>
                  <div className="info-box-title">Request Loan (Coming Soon)</div>
                  <p>
                    The loan request feature is under development. Use <b>Check Loan Credibility</b>{" "}
                    to see if you qualify for Normal, School Fees, or Emergency loans.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MemberLayout>
  );
}
